// Automatic cloud copy of progress. localStorage dies with a deleted
// Home-Screen icon, so the same payload as a Backup file is pushed to a
// Netlify blob (netlify/functions/progress.mjs) and pulled back silently on
// a fresh install. Ported from formosan-reader/seediq-reader's cloudSync.js.
//
// Multi-user: each device syncs under its own short code (blob key
// "latest-<code>"), so two people on the same app never overwrite each
// other. The code is generated at the first push and rides along inside the
// payload so a restored backup re-links its cloud slot. There is no Settings
// UI for it yet (this app has none) — sync is fully automatic, transparent,
// single-code-per-device; a future review hub reads/writes under this code.
const APP_ID = 'bible-app';

// The one list of keys that IS "the progress".
export const BACKUP_KEYS = [
  'seediq_schema_version', 'seediq_vocab_v1', 'seediq_progress_v1',
  'seediq_gloss_v1', 'seediq_sync_code_v1',
];

const FN = '/.netlify/functions/progress';
const LAST_PUSH = 'seediq_cloud_lastpush_v1';   // fingerprint of last successful push
const PULL_TRIED = 'seediq_cloud_pulled';       // one restore attempt per session — no reload loops
const SYNC_CODE = 'seediq_sync_code_v1';
const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';  // no 0/O 1/I/L lookalikes

export function getSyncCode() { return localStorage.getItem(SYNC_CODE) || ''; }

// A sync code names a storage slot; it is not a secret and guards nothing, so
// Math.random is an acceptable fallback where window.crypto is missing.
function randomCode() {
  const n = 6;
  const g = typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues
    ? Array.from(window.crypto.getRandomValues(new Uint8Array(n)))
    : Array.from({ length: n }, () => Math.floor(Math.random() * 256));
  return g.map((b) => ALPHABET[b % ALPHABET.length]).join('');
}

export function ensureSyncCode() {
  let c = getSyncCode();
  if (!c) {
    c = randomCode();
    localStorage.setItem(SYNC_CODE, c);
  }
  return c;
}

// "Has this device anything worth keeping?" — the guard on both pushing
// (never clobber the cloud with an empty state) and pulling (never restore
// over real work).
const REAL_KEYS = ['seediq_vocab_v1', 'seediq_gloss_v1'];

function hasRealProgress() {
  return REAL_KEYS.some((k) => {
    const v = localStorage.getItem(k);
    return !!v && v !== '{}';
  });
}

function payload() {
  const values = {};
  BACKUP_KEYS.forEach((k) => { const v = localStorage.getItem(k); if (v != null) values[k] = v; });
  return { app: APP_ID, saved: new Date().toISOString(), values };
}

export async function pushProgress() {
  try {
    if (!hasRealProgress()) return;
    const code = ensureSyncCode();   // into localStorage BEFORE payload() so it rides along
    const p = payload();
    const fingerprint = JSON.stringify(p.values);     // timestamp excluded on purpose
    if (localStorage.getItem(LAST_PUSH) === fingerprint) return;
    const r = await fetch(`${FN}?code=${code}`, { method: 'PUT', body: JSON.stringify(p), keepalive: true });
    if (r.ok) localStorage.setItem(LAST_PUSH, fingerprint);
  } catch (_) {} // offline — next tick will retry
}

// Fresh install: nothing here, but the cloud has something → restore and reload.
async function pullIfFresh() {
  try {
    if (hasRealProgress()) return;
    if (sessionStorage.getItem(PULL_TRIED)) return;
    sessionStorage.setItem(PULL_TRIED, '1');
    const code = getSyncCode();
    const r = await fetch(code ? `${FN}?code=${code}` : FN, { cache: 'no-store' });
    if (!r.ok) return;
    const data = await r.json();
    if (!data || data.app !== APP_ID || !data.values) return;
    const entries = Object.entries(data.values);
    if (!entries.length) return;
    entries.forEach(([k, v]) => localStorage.setItem(k, v));
    localStorage.setItem(LAST_PUSH, JSON.stringify(data.values));
    window.location.reload();
  } catch (_) {}
}

export function startCloudSync() {
  pullIfFresh().then(pushProgress);
  const t = setInterval(pushProgress, 90e3);
  // A hidden page may be the last we see of this session — flush now
  // (keepalive lets the PUT finish after the page is gone).
  const onVis = () => { if (document.visibilityState === 'hidden') pushProgress(); };
  document.addEventListener('visibilitychange', onVis);
  return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
}
